/**
 * The boot heartbeat: two consecutive controlled boots that never reach a
 * healthy state clear the offline cache by themselves.
 *
 * `sw.ts`'s network-first navigations mean a bad worker can no longer stop the
 * *shell* from loading. What it can still do is leave a page running new code
 * against data an old cache is still answering for — the skew that hung `/fly`
 * on "Starting engine…" on 2026-08-25 with the page itself perfectly alive.
 * The escape hatches (`Ctrl`+`Shift`+`Backspace`, `?unstale`) both need a user
 * who knows about them. This is the one that does not.
 *
 * The rule, and why it cannot fire on an ordinary slow load:
 *
 * - Only a **controlled** page is watched. An uncontrolled one has no worker
 *   answering for it, so a failure there is not a cache problem, and the
 *   counter is reset instead — which is also what makes the reload after a
 *   recovery clean the slate.
 * - Only a **watched route** is timed (`/` and `/fly`). Those are the two that
 *   report readiness, so those are the two whose silence means anything.
 * - A failure is recorded **only if the page is still open and still not
 *   healthy** when the deadline fires. Counting at boot *start* would count the
 *   user who opens the page and immediately navigates away — twice in a row,
 *   and their cache would be wiped for nothing. Counting at the deadline counts
 *   only a page that sat there and failed.
 * - The deadline is an **idle** one: every step the boot reports
 *   (`markBootProgress`) restarts it, so what is being timed is how long the
 *   boot has gone without saying anything, not how long it has taken.
 * - Reaching healthy resets the counter to zero, so the two failures have to
 *   be consecutive.
 *
 * ## Why the clock had to stop being a wall clock (2026-08-26)
 *
 * The original rule was a flat 30 s from page load, and it was wrong for the
 * reason a flat wall clock is always wrong here: **a slow boot is not a failed
 * boot.** `/fly`'s phases have a measured *mean* (`flight/boot-progress.ts`)
 * and a very wide spread — measured on this build at a 45× CPU throttle, a
 * boot that reached `ready` perfectly happily took 42 s, of which `airframe`
 * alone was 24.4 s against its 430 ms mean. That boot wrote a failure at 30 s.
 * It cleared it again on reaching `ready`, so it cost nothing *that* time; but
 * a user who gives up and reloads before the scene comes up leaves the failure
 * behind, and two of those in a row wipe every cache and force a 27 MB cold
 * install — which makes the next boot slower still.
 *
 * An idle deadline cannot make that mistake. A boot that is advancing says so;
 * a boot wedged by the code-vs-data skew this exists to heal says nothing at
 * all, for ever. And because the window now covers **one step** rather than a
 * whole boot it can afford to be generous: `BOOT_HEALTH_IDLE_MS` is 90 s of
 * complete silence, which no step of a healthy boot has ever come near and
 * which a wedged one will never break. The extension is bounded by the number
 * of steps a boot has (`/fly` reports seven), so a progressing boot cannot
 * postpone the watch indefinitely.
 *
 * Worst case for a false positive is still one cleared cache and a refetch,
 * which is a cost, not a break.
 */

import { unstale } from "./unstale.ts";

export const BOOT_FAILURE_KEY = "red-opus/offline-boot-failures";
/** Two, not one: a single unexplained failure is a bad afternoon, two in a row is a state. */
export const BOOT_FAILURE_LIMIT = 2;
/**
 * How long a watched boot may go **without reporting anything** before it counts
 * as failed. Not a budget for the boot: `markBootProgress` restarts it on every
 * step, so this is the silence that separates "slow" from "wedged".
 */
export const BOOT_HEALTH_IDLE_MS = 90_000;

/**
 * The routes with an asynchronous boot that can fail *after* the page renders
 * — the shell's asset load and intro (`/`) and the flight scene (`/fly`). Both
 * call `markBootHealthy` when they are up. Every other route renders
 * synchronously out of the app bundle and has nothing to wait for, so it is
 * not watched rather than being watched with no way to pass.
 */
export const WATCHED_ROUTES: readonly string[] = ["/", "/fly"];

export function isWatchedRoute(pathname: string): boolean {
  return WATCHED_ROUTES.includes(pathname);
}

/** The slice of `Storage` this needs; `undefined` where storage is refused outright. */
export type FailureStore = Pick<Storage, "getItem" | "setItem"> | undefined;

/** Total: anything unparseable, negative or absent reads as zero. */
export function readFailures(store: FailureStore): number {
  try {
    const raw = store?.getItem(BOOT_FAILURE_KEY);
    const count = raw === null || raw === undefined ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch {
    return 0;
  }
}

function writeFailures(store: FailureStore, count: number): void {
  try {
    store?.setItem(BOOT_FAILURE_KEY, String(count));
  } catch {
    // Private mode, quota, a policy-blocked origin: losing the counter costs
    // the auto-recovery, and must never cost the boot it is watching.
  }
}

export interface BootHealthDeps {
  readonly store: FailureStore;
  readonly pathname: string;
  /** Whether a service worker is currently controlling this page. */
  readonly controlled: boolean;
  readonly schedule: (run: () => void, ms: number) => ReturnType<typeof setTimeout>;
  readonly cancel: (handle: ReturnType<typeof setTimeout>) => void;
  /** What "two failures in a row" runs. `unstale()` in the app. */
  readonly recover: () => void;
}

export interface BootHealthWatch {
  /** Called by a route that has reached a state a user can fly from. */
  markHealthy(): void;
  /**
   * Called by a watched route each time its boot advances a step — a phase
   * report on `/fly`. Restarts the idle deadline, which is what keeps a boot
   * that is merely slow from being recorded as one that failed.
   */
  markProgress(): void;
  /** Whether this boot is being timed at all — for the tests, and for reading the rule back. */
  readonly watching: boolean;
}

const IDLE: BootHealthWatch = { markHealthy: () => undefined, markProgress: () => undefined, watching: false };

/** Arms the watch for one page load. Safe to call anywhere; it never throws. */
export function startBootHealthWatch(deps: BootHealthDeps): BootHealthWatch {
  if (!deps.controlled) {
    writeFailures(deps.store, 0);
    return IDLE;
  }
  if (!isWatchedRoute(deps.pathname)) return IDLE;

  const failures = readFailures(deps.store);
  if (failures >= BOOT_FAILURE_LIMIT) {
    // Cleared *before* recovering, not after: `unstale()` ends in a reload, and
    // a counter left at the limit would recover again on the next boot, and the
    // one after that.
    writeFailures(deps.store, 0);
    deps.recover();
    return IDLE;
  }

  // `settled` is what stops a late report from undoing a verdict: once the
  // deadline has written its failure, a step arriving afterwards must not
  // re-arm the timer (that would let one boot record two), and once the boot is
  // healthy there is nothing left to time.
  let settled = false;
  const record = (): void => {
    settled = true;
    writeFailures(deps.store, failures + 1);
  };
  let handle = deps.schedule(record, BOOT_HEALTH_IDLE_MS);
  return {
    markHealthy: () => {
      settled = true;
      deps.cancel(handle);
      writeFailures(deps.store, 0);
    },
    markProgress: () => {
      if (settled) return;
      deps.cancel(handle);
      handle = deps.schedule(record, BOOT_HEALTH_IDLE_MS);
    },
    watching: true,
  };
}

let current: BootHealthWatch | undefined;

/** The app's own watch, started once from `register-sw.ts`. */
export function installBootHealthWatch(): void {
  if (typeof window === "undefined") return;
  let store: FailureStore;
  try {
    store = window.localStorage;
  } catch {
    store = undefined;
  }
  current = startBootHealthWatch({
    store,
    pathname: window.location.pathname,
    controlled: typeof navigator !== "undefined" && navigator.serviceWorker?.controller != null,
    schedule: (run, ms) => setTimeout(run, ms),
    cancel: (handle) => clearTimeout(handle),
    recover: () => void unstale(),
  });
}

/**
 * "This page got where it was going." Called from the shell once its assets
 * are up (`shell-runtime.ts`) and from `/fly` when the scene reports `ready`.
 * A no-op when nothing is being watched, which is most of the time.
 */
export function markBootHealthy(): void {
  current?.markHealthy();
  current = undefined;
}

/**
 * "This page is still getting there." Called from `/fly` on every phase the
 * boot reports (`Fly.tsx`), which is the signal that separates a boot taking a
 * long time from a boot that has stopped. A no-op when nothing is watched.
 */
export function markBootProgress(): void {
  current?.markProgress();
}
